import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import Swal from 'sweetalert2'
import { Link } from 'react-router-dom'
import { PageTitle } from '../../../../../_metronic/layout/core'
import useAuth from '../../../../hooks/useAuth'
import { getSessionHistory } from '../../SellerQueue/services'
import { SellerServiceSessionItem } from '../../SellerQueue/types'
import { ReportShell } from '../components/ReportShell'
import { SellerIdentity } from '../components/SellerIdentity'
import { createDefaultSellerReportDateRange, useSellerReportFilters } from '../useSellerReportFilters'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

interface SellerPriorityRow {
  sellerId: number
  sellerLabel: string
  totalSessions: number
  conversionRate: number
  pendingCount: number
  pending24hPlus: number
  pending48hPlus: number
  avgPendingAgeHours: number
  pressureScore: number
}

const numberFormatter = new Intl.NumberFormat('pt-BR')
const percentFormatter = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})
const hourFormatter = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})

const parseDate = (value?: string | null): Date | null => {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

const getSellerLabel = (session: SellerServiceSessionItem): string => {
  const fallbackName = `Vendedor ${session.sellerId}`
  const rawName = session.sellerName?.trim()
  return rawName && rawName.length > 0 ? rawName : fallbackName
}

const toPercent = (value: number, total: number): number => {
  if (!total) return 0
  return (value / total) * 100
}

const exportToCSV = (filename: string, data: SellerPriorityRow[]): void => {
  const headers = [
    'Posição',
    'Vendedor',
    'Atendimentos',
    'Conversão (%)',
    'Pending',
    '24h+',
    '48h+',
    'Aging Médio (h)',
    'Score',
  ]

  const rows = data.map((row, index) => [
    index + 1,
    row.sellerLabel,
    row.totalSessions,
    row.conversionRate.toFixed(1),
    row.pendingCount,
    row.pending24hPlus,
    row.pending48hPlus,
    row.avgPendingAgeHours.toFixed(1),
    row.pressureScore.toFixed(1),
  ])

  const csv = [headers, ...rows].map((row) => row.map((cell) => `"${cell}"`).join(';')).join('\n')

  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)

  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  link.style.visibility = 'hidden'

  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const exportToXLSX = (filename: string, data: SellerPriorityRow[]): void => {
  const rows = data.map((row, index) => ({
    'Posição': index + 1,
    'Vendedor': row.sellerLabel,
    'Atendimentos': row.totalSessions,
    'Conversão (%)': row.conversionRate.toFixed(1),
    'Pending': row.pendingCount,
    '24h+': row.pending24hPlus,
    '48h+': row.pending48hPlus,
    'Aging Médio (h)': row.avgPendingAgeHours.toFixed(1),
    'Score': row.pressureScore.toFixed(1),
  }))

  const ws = XLSX.utils.json_to_sheet(rows)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Ranking')
  XLSX.writeFile(wb, filename)
}

const exportToPDF = (filename: string, data: SellerPriorityRow[], kpis: any): void => {
  const pdf = new jsPDF()
  const pageWidth = pdf.internal.pageSize.getWidth()
  const pageHeight = pdf.internal.pageSize.getHeight()
  const margin = 15
  let yPosition = margin

  // Header
  pdf.setFontSize(16)
  pdf.text('Painel Executivo Operacional', margin, yPosition)
  yPosition += 8

  pdf.setFontSize(10)
  pdf.setTextColor(100, 100, 100)
  const reportDate = new Date().toLocaleString('pt-BR')
  pdf.text(`Relatório gerado em: ${reportDate}`, margin, yPosition)
  yPosition += 12

  // KPIs Section
  pdf.setFontSize(11)
  pdf.setTextColor(0, 0, 0)
  pdf.text('Indicadores Consolidados', margin, yPosition)
  yPosition += 6

  pdf.setFontSize(9)
  const kpiText = [
    `Atendimentos: ${kpis.totalSessions} | Conversão: ${kpis.conversionRate.toFixed(1)}% | Pending: ${kpis.pendingCount}`,
    `Críticos 24h+: ${kpis.pending24hPlus} | Críticos 48h+: ${kpis.pending48hPlus} | Aging Médio: ${kpis.avgPendingAgeHours.toFixed(1)}h`,
  ]

  kpiText.forEach((line) => {
    pdf.text(line, margin, yPosition)
    yPosition += 5
  })

  yPosition += 5

  // Table
  const tableData = data.map((row, index) => [
    index + 1,
    row.sellerLabel,
    row.totalSessions,
    row.conversionRate.toFixed(1),
    row.pendingCount,
    row.pending24hPlus,
    row.pending48hPlus,
    row.avgPendingAgeHours.toFixed(1),
    row.pressureScore.toFixed(1),
  ])

  autoTable(pdf, {
    startY: yPosition,
    head: [
      [
        'Pos',
        'Vendedor',
        'Atend.',
        'Conv. (%)',
        'Pend.',
        '24h+',
        '48h+',
        'Aging (h)',
        'Score',
      ],
    ],
    body: tableData,
    margin: { left: margin, right: margin },
    headStyles: {
      fillColor: [41, 128, 185],
      textColor: 255,
      fontSize: 9,
      fontStyle: 'bold',
    },
    bodyStyles: {
      fontSize: 8,
      textColor: 50,
    },
    alternateRowStyles: {
      fillColor: [245, 245, 245],
    },
    columnStyles: {
      1: { halign: 'left' },
      2: { halign: 'center' },
      3: { halign: 'center' },
      4: { halign: 'center' },
      5: { halign: 'center' },
      6: { halign: 'center' },
      7: { halign: 'center' },
      8: { halign: 'center' },
    },
  })

  // Footer
  const pageCount = (pdf as any).internal.getNumberOfPages()
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i)
    pdf.setFontSize(8)
    pdf.setTextColor(150, 150, 150)
    pdf.text(
      `Página ${i} de ${pageCount}`,
      pageWidth / 2,
      pageHeight - 10,
      { align: 'center' }
    )
  }

  pdf.save(filename)
}

const SellerOperationalExecutiveReportPage: FC = () => {
  const { loginInfo } = useAuth()
  const pluginspaceId = Number(loginInfo.pluginspaceId || 0)

  const { filters, setFilters, isReady } = useSellerReportFilters({
    ...createDefaultSellerReportDateRange(),
  })
  const [loading, setLoading] = useState(false)
  const [sessions, setSessions] = useState<SellerServiceSessionItem[]>([])

  const loadReport = useCallback(async () => {
    if (!pluginspaceId || pluginspaceId <= 0) {
      setSessions([])
      return
    }

    try {
      setLoading(true)
      const data = await getSessionHistory(pluginspaceId, filters.startDate, filters.finalDate)
      setSessions(data)
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erro ao carregar relatório',
        text: error instanceof Error ? error.message : 'Erro desconhecido',
        confirmButtonText: 'OK',
      })
    } finally {
      setLoading(false)
    }
  }, [filters.finalDate, filters.startDate, pluginspaceId])

  useEffect(() => {
    if (isReady) {
      void loadReport()
    }
  }, [isReady, loadReport])

  const kpis = useMemo(() => {
    const totalSessions = sessions.length
    const completedSales = sessions.filter((session) => session.outcome === 'sale_completed').length
    const pendingCheckout = sessions.filter((session) => session.outcome === 'pending_checkout')

    const now = Date.now()
    const pendingAges = pendingCheckout.map((session) => {
      const referenceDate = parseDate(session.finishedAt) || parseDate(session.startedAt)
      return referenceDate ? Math.max(0, (now - referenceDate.getTime()) / 3600000) : 0
    })

    const pending24hPlus = pendingAges.filter((age) => age >= 24).length
    const pending48hPlus = pendingAges.filter((age) => age >= 48).length
    const avgPendingAgeHours = pendingAges.length
      ? pendingAges.reduce((acc, value) => acc + value, 0) / pendingAges.length
      : 0

    return {
      totalSessions,
      conversionRate: toPercent(completedSales, totalSessions),
      pendingCount: pendingCheckout.length,
      pending24hPlus,
      pending48hPlus,
      avgPendingAgeHours,
    }
  }, [sessions])

  const sellerPriority = useMemo<SellerPriorityRow[]>(() => {
    const now = Date.now()
    const grouped = new Map<number, SellerPriorityRow>()

    sessions.forEach((session) => {
      const current = grouped.get(session.sellerId)
      const row: SellerPriorityRow =
        current ||
        {
          sellerId: session.sellerId,
          sellerLabel: getSellerLabel(session),
          totalSessions: 0,
          conversionRate: 0,
          pendingCount: 0,
          pending24hPlus: 0,
          pending48hPlus: 0,
          avgPendingAgeHours: 0,
          pressureScore: 0,
        }

      row.totalSessions += 1

      if (session.outcome === 'sale_completed') {
        row.conversionRate += 1
      }

      if (session.outcome === 'pending_checkout') {
        row.pendingCount += 1
        const referenceDate = parseDate(session.finishedAt) || parseDate(session.startedAt)
        const ageHours = referenceDate ? Math.max(0, (now - referenceDate.getTime()) / 3600000) : 0

        row.avgPendingAgeHours += ageHours

        if (ageHours >= 24) row.pending24hPlus += 1
        if (ageHours >= 48) row.pending48hPlus += 1
      }

      grouped.set(session.sellerId, row)
    })

    return Array.from(grouped.values())
      .map((row) => {
        const conversionRate = toPercent(row.conversionRate, row.totalSessions)
        const avgPendingAgeHours = row.pendingCount ? row.avgPendingAgeHours / row.pendingCount : 0

        // Synthetic urgency score to guide daily action.
        const pressureScore =
          row.pendingCount * 8 +
          row.pending24hPlus * 10 +
          row.pending48hPlus * 16 +
          avgPendingAgeHours * 0.7 +
          Math.max(0, 100 - conversionRate) * 0.25

        return {
          ...row,
          conversionRate,
          avgPendingAgeHours,
          pressureScore,
        }
      })
      .sort((left, right) => right.pressureScore - left.pressureScore)
  }, [sessions])

  const top5 = sellerPriority.slice(0, 5)

  return (
    <>
      <PageTitle
        breadcrumbs={[
          { title: 'Painel Executivo Operacional', path: '/seller/reports/operational-executive', isActive: true },
        ]}
      >
        Painel Executivo Operacional
      </PageTitle>

      <ReportShell
        title='Painel Executivo Operacional de Vendedores'
        description='Consolida conversão, aging e backlog crítico em uma visão única para decisão rápida de gestão.'
        actions={
          <div className='d-flex gap-2'>
            <button className='btn btn-primary' onClick={loadReport} disabled={loading}>
              <i className='fas fa-sync-alt me-2'></i>
              {loading ? 'Carregando...' : 'Atualizar'}
            </button>
            <button
              className='btn btn-light-success'
              onClick={() => {
                const filename = `Ranking-Vendedores-${new Date().toISOString().split('T')[0]}.csv`
                exportToCSV(filename, sellerPriority)
              }}
              disabled={loading || sellerPriority.length === 0}
              title='Baixar ranking em CSV'
            >
              <i className='fas fa-file-csv me-2'></i>
              CSV
            </button>
            <button
              className='btn btn-light-info'
              onClick={() => {
                const filename = `Ranking-Vendedores-${new Date().toISOString().split('T')[0]}.xlsx`
                exportToXLSX(filename, sellerPriority)
              }}
              disabled={loading || sellerPriority.length === 0}
              title='Baixar ranking em XLSX'
            >
              <i className='fas fa-file-excel me-2'></i>
              XLSX
            </button>
            <button
              className='btn btn-light-danger'
              onClick={() => {
                const filename = `Ranking-Vendedores-${new Date().toISOString().split('T')[0]}.pdf`
                exportToPDF(filename, sellerPriority, kpis)
              }}
              disabled={loading || sellerPriority.length === 0}
              title='Baixar ranking em PDF'
            >
              <i className='fas fa-file-pdf me-2'></i>
              PDF
            </button>
          </div>
        }
        filters={
          <div className='row g-3 align-items-end'>
            <div className='col-12 col-md-5'>
              <label className='form-label fw-semibold'>Data inicial</label>
              <input
                className='form-control'
                type='date'
                value={filters.startDate}
                onChange={(event) =>
                  setFilters((previous) => ({ ...previous, startDate: event.target.value }))
                }
              />
            </div>
            <div className='col-12 col-md-5'>
              <label className='form-label fw-semibold'>Data final</label>
              <input
                className='form-control'
                type='date'
                value={filters.finalDate}
                onChange={(event) =>
                  setFilters((previous) => ({ ...previous, finalDate: event.target.value }))
                }
              />
            </div>
            <div className='col-12 col-md-2 d-grid'>
              <button className='btn btn-light-primary' onClick={loadReport} disabled={loading}>
                Buscar
              </button>
            </div>
          </div>
        }
      >
        {!pluginspaceId ? (
          <div className='alert alert-warning mb-0'>Pluginspace nao identificado para o usuario logado.</div>
        ) : (
          <>
            <div className='row g-4 mb-4'>
              <div className='col-12 col-md-6 col-xl-2'>
                <div className='card border-0 shadow-sm h-100'>
                  <div className='card-body'>
                    <div className='text-gray-600 fw-semibold mb-1'>Atendimentos</div>
                    <div className='fs-2hx fw-bold text-primary'>{numberFormatter.format(kpis.totalSessions)}</div>
                  </div>
                </div>
              </div>
              <div className='col-12 col-md-6 col-xl-2'>
                <div className='card border-0 shadow-sm h-100'>
                  <div className='card-body'>
                    <div className='text-gray-600 fw-semibold mb-1'>Conversão</div>
                    <div className='fs-2hx fw-bold text-success'>{percentFormatter.format(kpis.conversionRate)}%</div>
                  </div>
                </div>
              </div>
              <div className='col-12 col-md-6 col-xl-2'>
                <div className='card border-0 shadow-sm h-100'>
                  <div className='card-body'>
                    <div className='text-gray-600 fw-semibold mb-1'>Pending</div>
                    <div className='fs-2hx fw-bold text-warning'>{numberFormatter.format(kpis.pendingCount)}</div>
                  </div>
                </div>
              </div>
              <div className='col-12 col-md-6 col-xl-2'>
                <div className='card border-0 shadow-sm h-100'>
                  <div className='card-body'>
                    <div className='text-gray-600 fw-semibold mb-1'>24h+</div>
                    <div className='fs-2hx fw-bold text-warning'>{numberFormatter.format(kpis.pending24hPlus)}</div>
                  </div>
                </div>
              </div>
              <div className='col-12 col-md-6 col-xl-2'>
                <div className='card border-0 shadow-sm h-100'>
                  <div className='card-body'>
                    <div className='text-gray-600 fw-semibold mb-1'>48h+</div>
                    <div className='fs-2hx fw-bold text-danger'>{numberFormatter.format(kpis.pending48hPlus)}</div>
                  </div>
                </div>
              </div>
              <div className='col-12 col-md-6 col-xl-2'>
                <div className='card border-0 shadow-sm h-100'>
                  <div className='card-body'>
                    <div className='text-gray-600 fw-semibold mb-1'>Aging médio</div>
                    <div className='fs-2 fw-bold text-dark'>{hourFormatter.format(kpis.avgPendingAgeHours)}h</div>
                  </div>
                </div>
              </div>
            </div>

            <div className='row g-4 mb-4'>
              <div className='col-12 col-xl-7'>
                <div className='card border-0 shadow-sm h-100'>
                  <div className='card-header border-0 pt-5'>
                    <div className='card-title d-flex flex-column'>
                      <span className='fs-3 fw-bold text-dark'>Top 5 Prioridades</span>
                      <span className='text-gray-600 mt-1'>Vendedores com maior score de pressão operacional.</span>
                    </div>
                  </div>
                  <div className='card-body pt-0'>
                    {top5.length > 0 ? (
                      <div className='table-responsive'>
                        <table className='table align-middle table-row-dashed fs-6 gy-3'>
                          <thead>
                            <tr className='text-start text-gray-600 fw-bold fs-7 text-uppercase gs-0'>
                              <th>#</th>
                              <th>Vendedor</th>
                              <th className='text-end'>Conversão</th>
                              <th className='text-end'>Pending</th>
                              <th className='text-end'>48h+</th>
                              <th className='text-end'>Score</th>
                            </tr>
                          </thead>
                          <tbody>
                            {top5.map((row, index) => (
                              <tr key={row.sellerId}>
                                <td>#{index + 1}</td>
                                <td className='fw-bold text-dark'>
                                  <SellerIdentity label={row.sellerLabel} />
                                </td>
                                <td className='text-end'>{percentFormatter.format(row.conversionRate)}%</td>
                                <td className='text-end'>{numberFormatter.format(row.pendingCount)}</td>
                                <td className='text-end text-danger fw-semibold'>{numberFormatter.format(row.pending48hPlus)}</td>
                                <td className='text-end fw-bold'>{hourFormatter.format(row.pressureScore)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className='alert alert-light-info mb-0'>Sem dados para o período selecionado.</div>
                    )}
                  </div>
                </div>
              </div>

              <div className='col-12 col-xl-5'>
                <div className='card border-0 shadow-sm h-100'>
                  <div className='card-header border-0 pt-5'>
                    <div className='card-title d-flex flex-column'>
                      <span className='fs-3 fw-bold text-dark'>Atalhos de Ação</span>
                      <span className='text-gray-600 mt-1'>Navegação rápida para execução operacional.</span>
                    </div>
                  </div>
                  <div className='card-body pt-0 d-flex flex-column gap-3'>
                    {/* Desativado: outcome 'pending_checkout' não é gravado por nenhuma ação do fluxo atual (ver ReturnToQueueModal) */}
                    {/* <Link to='/seller/reports/pending-checkout-followup' className='btn btn-light-danger text-start'>
                      Follow-up Backlog por Vendedor
                    </Link> */}
                    {/* <Link to='/seller/reports/pending-checkout-aging' className='btn btn-light-warning text-start'>
                      Tempo de permanência em checkout pendente
                    </Link> */}
                    {/* <Link to='/seller/reports/conversion-aging-correlation' className='btn btn-light-primary text-start'>
                      Comparação Conversão x Tempo de Permanência
                    </Link> */}
                    <Link to='/seller/reports/conversion-rate' className='btn btn-light-success text-start'>
                      Taxa de Conversão por Atendimento
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            <div className='card border-0 shadow-sm'>
              <div className='card-header border-0 pt-5'>
                <div className='card-title d-flex flex-column'>
                  <span className='fs-3 fw-bold text-dark'>Ranking completo por score</span>
                  <span className='text-gray-600 mt-1'>Usar como fila de priorização para gestão e coordenação comercial.</span>
                </div>
              </div>
              <div className='card-body pt-0'>
                {loading ? (
                  <div className='d-flex justify-content-center align-items-center py-10'>
                    <div className='spinner-border text-primary' role='status'>
                      <span className='visually-hidden'>Carregando...</span>
                    </div>
                  </div>
                ) : sellerPriority.length > 0 ? (
                  <div className='table-responsive'>
                    <table className='table align-middle table-row-dashed fs-6 gy-3'>
                      <thead>
                        <tr className='text-start text-gray-600 fw-bold fs-7 text-uppercase gs-0'>
                          <th>Posição</th>
                          <th>Vendedor</th>
                          <th className='text-end'>Atendimentos</th>
                          <th className='text-end'>Conversão</th>
                          <th className='text-end'>Pending</th>
                          <th className='text-end'>24h+</th>
                          <th className='text-end'>48h+</th>
                          <th className='text-end'>Aging médio</th>
                          <th className='text-end'>Score</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sellerPriority.map((row, index) => (
                          <tr key={row.sellerId}>
                            <td>#{index + 1}</td>
                            <td className='fw-bold text-dark'>
                              <SellerIdentity label={row.sellerLabel} />
                            </td>
                            <td className='text-end'>{numberFormatter.format(row.totalSessions)}</td>
                            <td className='text-end text-success fw-semibold'>{percentFormatter.format(row.conversionRate)}%</td>
                            <td className='text-end text-warning fw-semibold'>{numberFormatter.format(row.pendingCount)}</td>
                            <td className='text-end'>{numberFormatter.format(row.pending24hPlus)}</td>
                            <td className='text-end text-danger fw-semibold'>{numberFormatter.format(row.pending48hPlus)}</td>
                            <td className='text-end'>{hourFormatter.format(row.avgPendingAgeHours)}h</td>
                            <td className='text-end fw-bold'>{hourFormatter.format(row.pressureScore)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className='alert alert-light-info mb-0'>Sem dados para o período selecionado.</div>
                )}
              </div>
            </div>
          </>
        )}
      </ReportShell>
    </>
  )
}

export { SellerOperationalExecutiveReportPage }

